/**
 * Folders API hooks - Global sync via Jotai + TanStack Query mutations
 *
 * Architecture:
 * - useFoldersSync() subscribes to Firestore and populates foldersStateAtom
 * - Returns isLoading=true until first snapshot, used by app initialization
 * - All consumers read from the shared Jotai atom via useFoldersQuery()
 */
import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import type { DocumentData } from 'firebase/firestore';
import { collection, query } from 'firebase/firestore';
import { useAtomValue } from 'jotai';

import { useFirestoreCollectionSync } from '@/hooks/useFirestoreSync';
import { auth, firestore } from '@/services/firebase';
import { createFolder, deleteFolder, reorderFolders, updateFolder } from '@/services/folders';
import type { Folder } from '@/types/card';
import type { CreateFolder } from '@/types/schemas';
import { foldersStateAtom } from '../state/foldersAtom';

// Re-export state atom for consumers who need direct access
export { foldersStateAtom } from '../state/foldersAtom';

// ============================================================================
// Sync Hook
// ============================================================================

/**
 * Syncs folders from Firestore to the foldersStateAtom.
 * Returns true while loading (until first snapshot arrives).
 *
 * @param enabled - Whether sync is enabled (typically: isAuthenticated)
 *
 * @example
 * ```tsx
 * const isLoadingFolders = useFoldersSync(isAuthenticated);
 * ```
 */
export function useFoldersSync(enabled: boolean): boolean {
  const transform = useCallback((docs: DocumentData[]): Folder[] => {
    return (docs as Folder[]).sort((a, b) => {
      if (a.sort_order !== b.sort_order) {
        return a.sort_order - b.sort_order;
      }
      return a.name.localeCompare(b.name);
    });
  }, []);

  const queryFactory = useCallback(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) return null;
    return query(collection(firestore, 'users', userId, 'folders'));
  }, []);

  return useFirestoreCollectionSync({
    atom: foldersStateAtom,
    transform,
    queryFactory,
    enabled,
  });
}

// ============================================================================
// Public Hooks
// ============================================================================

/**
 * Read-only hook for user's folders.
 * Returns the current folders state from the shared Jotai atom.
 * Data is populated by init and kept in sync by useFoldersSubscription.
 */
export function useFoldersQuery() {
  return useAtomValue(foldersStateAtom);
}

/**
 * Mutation hook for creating a folder
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useCreateFolderMutation() {
  return useMutation({
    mutationFn: (data: CreateFolder) => createFolder(data),
  });
}

/**
 * Mutation hook for updating a folder
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useUpdateFolderMutation() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Omit<Folder, 'id' | 'created_at' | 'updated_at'>> }) => updateFolder(id, data),
  });
}

/**
 * Mutation hook for deleting a folder
 * Note: No cache invalidation needed - real-time subscription auto-updates
 * (Collection subscription will pick up folder_id changes on affected cards)
 */
export function useDeleteFolderMutation() {
  return useMutation({
    mutationFn: (folderId: string) => deleteFolder(folderId),
  });
}

/**
 * Mutation hook for reordering folders
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useReorderFoldersMutation() {
  return useMutation({
    mutationFn: (folderIds: string[]) => reorderFolders(folderIds),
  });
}
